import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget({ enabled, greeting }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() =>
    "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36),
  );
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0 && greeting) {
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [open, greeting, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  if (!enabled) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages([...next, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, messages: next }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Chat error");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          const evType = lines.find((l) => l.startsWith("event:"))?.slice(6).trim();
          const dataLine = lines.find((l) => l.startsWith("data:"))?.slice(5).trim() || "";
          if (evType === "done") return;
          if (evType === "error") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry — something went wrong. Please call 801-347-2220 or use the quote form." };
              return copy;
            });
            return;
          }
          if (dataLine) {
            const chunk = dataLine.replace(/\\n/g, "\n");
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, content: (last.content || "") + chunk };
              return copy;
            });
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: err.message || "Sorry — I'm offline right now. Please call 801-347-2220 or use the quote form.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-50 bg-navy hover:bg-navy-deep text-white w-14 h-14 rounded-full grid place-items-center elev-3 transition-transform hover:scale-105"
          aria-label="Open chat assistant"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 right-5 z-50 w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] bg-white border border-border rounded-2xl elev-3 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-navy text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 grid place-items-center bg-sunrise rounded-full">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-display font-bold leading-none">Five Peaks Assistant</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">Online</div>
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-secondary/40">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-sunrise text-white rounded-br-md"
                      : "bg-white border border-border text-navy rounded-bl-md elev-1"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-border bg-white p-3 flex items-center gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, area, pricing…"
              disabled={streaming}
              className="flex-1 px-3 py-2 text-sm border border-border rounded-xl focus:outline-none focus:border-sunrise focus:ring-1 focus:ring-sunrise"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={streaming || !input.trim()}
              className="bg-sunrise hover:bg-sunrise-hover disabled:opacity-50 text-white w-10 h-10 rounded-xl grid place-items-center transition-colors"
              aria-label="Send"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
